import React, { useCallback, useMemo } from "react"

import { useAgentMutationsList } from "@/agents/agent-mutations"
import { useAgentsById, useAgentsList } from "@/agents/catalog"
import { ENTITY_ICONS } from "@/constants/entity-icons"
import { Badge, Checkbox, Skeleton } from "@coval/ui"
import { Info } from "lucide-react"

import { EntitySelectionCard } from "@/components/entity-picker/EntitySelectionCard"
import { useEntityPickerDraft } from "@/components/entity-picker/useEntityPickerDraft"
import {
  SimulatorType as SimulatorTypeEnum,
  type SimulatorType,
} from "@/types/api/SimulatorType"
import type { SimulationMetadata } from "@/types/backend-contracts"

import type { AgentMutation } from "../agent-mutations/types"
import { getAllChangedFields } from "../agent-mutations/utils/getChangedFields"
import { buildAgentSimulationMetadata } from "../utils/agentSimulationMetadata"
import { AgentPickerDialog } from "./AgentPickerDialog"

const AgentIcon = ENTITY_ICONS.agents

interface AgentMutationsSectionProps {
  agentId: string
  agentMetadata: Record<string, unknown>
  selectedMutationIds: string[]
  onToggleMutation: (mutationId: string) => void
  onSelectAll: (mutations: AgentMutation[]) => void
  disabled: boolean
}

const AgentMutationsSection: React.FC<AgentMutationsSectionProps> = ({
  agentId,
  agentMetadata,
  selectedMutationIds,
  onToggleMutation,
  onSelectAll,
  disabled,
}) => {
  const { data: allMutations, isLoading } = useAgentMutationsList(agentId, {
    enabled: !!agentId,
  })

  const mutations = useMemo(() => {
    return (allMutations || []).filter((m) => m.status === "ACTIVE")
  }, [allMutations])

  const selectAllState = useMemo(() => {
    if (mutations.length === 0) {
      return { allSelected: false, someSelected: false }
    }
    const allMutationIds = mutations.map((m) => m.id)
    const selectedCount = allMutationIds.filter((id) =>
      selectedMutationIds.includes(id),
    ).length
    return {
      allSelected: selectedCount === allMutationIds.length,
      someSelected: selectedCount > 0 && selectedCount < allMutationIds.length,
    }
  }, [mutations, selectedMutationIds])

  if (isLoading) {
    return (
      <div className="flex items-center p-2">
        <Skeleton className="h-4 w-16 rounded-md" />
      </div>
    )
  }

  if (mutations.length === 0) {
    return null
  }

  return (
    <>
      <div className="border-t border-coval-border" />
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-coval-text">
            Agent Mutations
          </h4>
          <Badge size="status">Optional</Badge>
        </div>
        <p className="text-xs text-coval-text-muted">
          Select configuration variants to run alongside base agent
        </p>
        <div className="overflow-y-auto max-h-[300px]">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-surface-0">
              <Checkbox checked disabled aria-label="Base Agent always runs" />
              <span className="text-sm text-coval-text">Base Agent</span>
              <Badge size="status" variant="success">
                always runs
              </Badge>
            </div>

            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-surface-0 transition-colors">
              <Checkbox
                checked={
                  selectAllState.allSelected
                    ? true
                    : selectAllState.someSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={() => onSelectAll(mutations)}
                disabled={disabled}
                aria-label="Select all mutations"
              />
              <span className="text-sm text-coval-text">
                Select All Mutations
              </span>
            </div>

            {mutations.map((mutation) => {
              const changedFields = getAllChangedFields(
                mutation.config_overrides || {},
                agentMetadata,
              )
              return (
                <div
                  key={mutation.id}
                  className="flex flex-wrap items-center gap-2 p-2 rounded-lg hover:bg-surface-0 transition-colors"
                >
                  <Checkbox
                    checked={selectedMutationIds.includes(mutation.id)}
                    onCheckedChange={() => onToggleMutation(mutation.id)}
                    disabled={disabled}
                    aria-label={mutation.display_name}
                  />
                  <span className="text-sm text-coval-text">
                    {mutation.display_name}
                  </span>
                  {changedFields.map((field) => (
                    <Badge key={field.path} size="status">
                      {field.label}
                    </Badge>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

interface SimpleAgentFeatureProps {
  selectedAgentIds: string[]
  simulatorType: SimulatorType
  simulationMetadata: SimulationMetadata
  onAgentIdsChange: (agentIds: string[]) => void
  onSimulatorChange: (type: SimulatorType, metadata: SimulationMetadata) => void
  disabled?: boolean
  readOnly?: boolean
  personaCount?: number
  selectedMutationIds?: Record<string, string[]>
  onMutationIdsChange?: (mutations: Record<string, string[]>) => void
  tagColorMap?: Record<string, string | null | undefined>
  showMultiRunWarning?: boolean
  /**
   * Optional action offered inside the agent picker, for a caller that can
   * create one, so a user with no agents is not stuck at an empty catalog.
   */
  footer?: React.ReactNode
}

export const AgentSelector: React.FC<SimpleAgentFeatureProps> = ({
  selectedAgentIds,
  onAgentIdsChange,
  onSimulatorChange,
  disabled = false,
  readOnly = false,
  personaCount = 0,
  selectedMutationIds,
  onMutationIdsChange,
  tagColorMap,
  showMultiRunWarning = true,
  footer,
}) => {
  const {
    data: agents,
    isLoading,
    isError,
  } = useAgentsList({
    // Add-agent navigates away to /agents/create and back, unmounting this
    // selector while the new agent is created. Invalidation on create only
    // refetches active (mounted) observers, so without this override the
    // remounted selector would serve the stale pre-create list until a hard
    // refresh (mirrors the fix for the test-set dropdown in FE-669).
    refetchOnMount: true,
  })
  const { agentsById } = useAgentsById({
    // Same staleness as the listAgents override above: this lookup backs
    // firstAgentMetadata below, and without forcing it fresh a just-created
    // agent selected immediately after remount would resolve to undefined
    // metadata here even though it now appears in the list.
    refetchOnMount: true,
  })

  const isDisabled = disabled || readOnly
  const showMutations =
    selectedAgentIds.length === 1 &&
    selectedMutationIds !== undefined &&
    onMutationIdsChange !== undefined

  const handleValuesChange = useCallback(
    (newIds: string[]) => {
      if (newIds.length === 0) {
        onSimulatorChange(SimulatorTypeEnum.NOOP, {})
      } else if (newIds[0] !== selectedAgentIds[0]) {
        // The run's simulator and metadata come from the leading agent, and the
        // picker commits a whole selection at once — swapping the only agent,
        // or picking several from an empty selection, both change which agent
        // leads without passing through the empty state. Keying off the leading
        // agent keeps the simulator with the selection it describes.
        const agent = agents?.find((a) => a.id === newIds[0])
        if (agent) {
          onSimulatorChange(
            agent.model_type as SimulatorType,
            buildAgentSimulationMetadata(agent),
          )
        }
      }
      onAgentIdsChange(newIds)
    },
    [agents, selectedAgentIds, onAgentIdsChange, onSimulatorChange],
  )

  const picker = useEntityPickerDraft(selectedAgentIds, handleValuesChange, {
    disabled: isDisabled,
  })

  const { draftIds } = picker

  const selectedAgentObjects = useMemo(() => {
    return agents?.filter((a) => selectedAgentIds.includes(a.id)) ?? []
  }, [agents, selectedAgentIds])

  const missingSelectedIds = useMemo(() => {
    if (!agents) {
      return selectedAgentIds
    }
    const agentIdSet = new Set(agents.map((a) => a.id))
    return selectedAgentIds.filter((id) => !agentIdSet.has(id))
  }, [agents, selectedAgentIds])

  const selectedAgentViewRows = useMemo(
    () => [
      ...selectedAgentObjects.map((agent) => ({
        id: agent.id,
        name: agent.display_name,
      })),
      ...missingSelectedIds.map((id) => ({ id, name: undefined })),
    ],
    [missingSelectedIds, selectedAgentObjects],
  )
  const selectedAgentPrimary =
    selectedAgentViewRows.length === 1 ? selectedAgentViewRows[0] : null
  const hasMultipleSelectedAgents = selectedAgentViewRows.length > 1

  // Chips follow the committed selection order, and an id with no matching
  // record still gets one so it can be removed.
  const chips = useMemo(
    () =>
      selectedAgentIds.map((id) => {
        const agent = selectedAgentObjects.find((a) => a.id === id)
        return {
          id,
          label: agent?.display_name || id,
          isMissing: !agent && !isLoading,
          // Sofia reads the chip row as the card's list of agents.
          attributes: hasMultipleSelectedAgents
            ? {
                "data-covi-highlight-id": id,
                "data-covi-name": agent?.display_name || undefined,
              }
            : undefined,
        }
      }),
    [
      hasMultipleSelectedAgents,
      isLoading,
      selectedAgentIds,
      selectedAgentObjects,
    ],
  )

  const handleRemoveAgent = useCallback(
    (agentId: string) => {
      handleValuesChange(selectedAgentIds.filter((id) => id !== agentId))
    },
    [selectedAgentIds, handleValuesChange],
  )

  const totalRunCount = useMemo(() => {
    if (selectedAgentIds.length === 0) {
      return 0
    }
    return selectedAgentIds.length * Math.max(personaCount, 1)
  }, [selectedAgentIds.length, personaCount])

  const handleToggleMutation = useCallback(
    (agentId: string, mutationId: string) => {
      if (!selectedMutationIds || !onMutationIdsChange) {
        return
      }
      const currentIds = selectedMutationIds[agentId] || []
      const isSelected = currentIds.includes(mutationId)
      const newIds = isSelected
        ? currentIds.filter((id) => id !== mutationId)
        : [...currentIds, mutationId]
      onMutationIdsChange({ ...selectedMutationIds, [agentId]: newIds })
    },
    [selectedMutationIds, onMutationIdsChange],
  )

  const handleSelectAllMutations = useCallback(
    (agentId: string, mutations: AgentMutation[]) => {
      if (!selectedMutationIds || !onMutationIdsChange) {
        return
      }
      const currentIds = selectedMutationIds[agentId] || []
      const allMutationIds = mutations.map((m) => m.id)
      const allSelected = allMutationIds.every((id) => currentIds.includes(id))
      onMutationIdsChange({
        ...selectedMutationIds,
        [agentId]: allSelected ? [] : allMutationIds,
      })
    },
    [selectedMutationIds, onMutationIdsChange],
  )

  const firstAgentId = selectedAgentIds[0]
  const firstAgent = firstAgentId ? agentsById[firstAgentId] : undefined
  const firstAgentMetadata = (firstAgent?.metadata || {}) as Record<
    string,
    unknown
  >

  return (
    <>
      <EntitySelectionCard
        icon={AgentIcon}
        title="Agent"
        chips={chips}
        onRemove={handleRemoveAgent}
        onBrowse={picker.open}
        onClearAll={() => handleValuesChange([])}
        browseLabel="Browse Agents"
        addLabel="Add Agents"
        emptyMessage={isError ? "Failed to load agents" : undefined}
        browseTestId="agent-browse-button"
        disabled={isDisabled}
        cardProps={
          {
            "data-covi-view": selectedAgentPrimary
              ? JSON.stringify({
                  kind: "agent",
                  id: selectedAgentPrimary.id,
                  name: selectedAgentPrimary.name,
                })
              : undefined,
            "data-covi-view-list": hasMultipleSelectedAgents
              ? "agent"
              : undefined,
            "data-covi-list-total": hasMultipleSelectedAgents
              ? selectedAgentViewRows.length
              : undefined,
            "data-covi-list-showing": hasMultipleSelectedAgents
              ? `${selectedAgentViewRows.length} selected agents`
              : undefined,
          } as React.HTMLAttributes<HTMLDivElement>
        }
      >
        {showMultiRunWarning && selectedAgentIds.length > 1 && (
          <div className="p-4 rounded-lg bg-coval-info-surface border border-coval-info-border">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-coval-info-on-surface mt-0.5 shrink-0" />
              <p className="text-sm font-semibold text-coval-info-on-surface">
                {selectedAgentIds.length} agents
                {personaCount > 0 && ` × ${personaCount} personas`} ={" "}
                {totalRunCount} runs
              </p>
            </div>
          </div>
        )}

        {showMutations && firstAgentId && (
          <AgentMutationsSection
            agentId={firstAgentId}
            agentMetadata={firstAgentMetadata}
            selectedMutationIds={selectedMutationIds?.[firstAgentId] || []}
            onToggleMutation={(mutationId) =>
              handleToggleMutation(firstAgentId, mutationId)
            }
            onSelectAll={(mutations) =>
              handleSelectAllMutations(firstAgentId, mutations)
            }
            disabled={isDisabled}
          />
        )}
      </EntitySelectionCard>
      <AgentPickerDialog
        isOpen={picker.isOpen}
        onCancel={picker.cancel}
        onApply={picker.apply}
        agents={agents ?? []}
        isLoading={isLoading}
        selectedIds={draftIds}
        onSelectionChange={picker.setDraftIds}
        tagColorMap={tagColorMap}
        createAction={footer}
      />
    </>
  )
}
